import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { fetchEventSource } from '@microsoft/fetch-event-source';
import axios, { AxiosError } from 'axios';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { HttpHeaders } from '~/constants';
import { ErrorResponse } from '~/infra/error-response/ErrorResponse';
import { FatalStreamError } from './agenticStreamError';
import { sendWithReplays } from './requestReplay';
import { selectors } from './state-selectors';
export var APIHost = /*#__PURE__*/ function(APIHost) {
    APIHost["AI"] = "ai";
    APIHost["KYB"] = "kyb";
    APIHost["Plaid"] = "plaid";
    APIHost["Scout"] = "scout";
    APIHost["Mgmt"] = "mgmt";
    APIHost["Cato"] = "cato";
    APIHost["External"] = "external";
    return APIHost;
}({});
// WHY only reads: Envoy kills an idle keepalive connection mid-request, but replaying a write can double-charge.
const READ_METHODS = [
    'get',
    'query'
];
const URL_BUSINESS_ID_REGEX = /\/businesses?\/(business-[a-zA-Z0-9_-]+)(?:\/|$|\?)/;
function isUrlBusinessIdStale(url, currentClaimsBusinessId) {
    if (currentClaimsBusinessId == null) return false;
    const match = url.match(URL_BUSINESS_ID_REGEX);
    const urlBusinessId = match === null || match === void 0 ? void 0 : match[1];
    if (urlBusinessId == null) return false;
    return urlBusinessId !== currentClaimsBusinessId;
}
function buildRequestInputs(options, state, getCookie) {
    const { apiHost, url, disableAuth, forceAuthorizationHeader, authToken } = options;
    const { apiGatewayHost, useAuthorizationHeader } = state.session;
    const fullUrl = apiGatewayHost + url;
    const prevCookie = getCookie();
    const headers = {};
    headers[HttpHeaders.ContentType] = 'application/json';
    const apiHostHeaderHosts = [
        "plaid",
        "scout",
        "kyb",
        "cato",
        "ai",
        "mgmt"
    ];
    if (apiHostHeaderHosts.includes(apiHost)) {
        headers['x-api-host'] = apiHost;
    }
    const actualAuthToken = (disableAuth || !useAuthorizationHeader) && !forceAuthorizationHeader ? null : authToken !== null && authToken !== void 0 ? authToken : state.authToken;
    if (actualAuthToken) {
        headers.Authorization = "Bearer ".concat(actualAuthToken);
    }
    if (window._sessionId) {
        headers[HttpHeaders.SessionId] = window._sessionId;
    }
    const requestId = generateUniqueId('request');
    headers[HttpHeaders.RequestId] = requestId;
    return {
        fullUrl,
        headers,
        requestId,
        prevCookie,
        sendsCookies: !useAuthorizationHeader
    };
}
function prepareRequest(options, state, getCookie) {
    var _config_headers, _state_claims, _state_userInfo;
    const { method, params = {}, data = {}, config } = options;
    const { fullUrl, headers, requestId, prevCookie, sendsCookies } = buildRequestInputs(options, state, getCookie);
    const mergedHeaders = _object_spread({}, config === null || config === void 0 ? void 0 : config.headers, headers);
    if (config === null || config === void 0 ? void 0 : (_config_headers = config.headers) === null || _config_headers === void 0 ? void 0 : _config_headers[HttpHeaders.ContentType]) {
        mergedHeaders[HttpHeaders.ContentType] = config.headers[HttpHeaders.ContentType];
    }
    return {
        options,
        fullUrl,
        requestId,
        prevCookie,
        authTokenAtSend: state.authToken,
        identityAtSend: {
            claimsBusinessId: ((_state_claims = state.claims) === null || _state_claims === void 0 ? void 0 : _state_claims.businessId) || null,
            userInfoBusinessId: ((_state_userInfo = state.userInfo) === null || _state_userInfo === void 0 ? void 0 : _state_userInfo.businessId()) || null,
            isSwitchingBusiness: !!state.isSwitchingBusiness
        },
        axiosConfig: _object_spread_props(_object_spread({
            method,
            url: fullUrl
        }, config), {
            withCredentials: sendsCookies,
            params,
            headers: mergedHeaders,
            data
        })
    };
}
function wasSentWithCurrentToken(param, state) {
    let { authTokenAtSend } = param;
    return state.authToken === authTokenAtSend;
}
const RATE_LIMIT_RECOVERED = 'rate_limit_recovered';
const rateLimitRecoveredEvent = (param, param1)=>{
    let { method, url, urlForSentry } = param, { attempts, retryAfterMs } = param1;
    return {
        type: RATE_LIMIT_RECOVERED,
        page_name: RATE_LIMIT_RECOVERED,
        hierarchy: [],
        method,
        path: urlForSentry !== null && urlForSentry !== void 0 ? urlForSentry : url,
        attempts,
        retry_after_ms: retryAfterMs
    };
};
function authTokenInBody(body) {
    if (body == null || typeof body !== 'object') return null;
    const { authToken } = body;
    return typeof authToken === 'string' && authToken.length > 0 ? authToken : null;
}
export function initSendRequest(param) {
    let { getState, getCookie, onCookieChange, onLogout, onNoAccess, onReportToSentry, onAuthTokenInResponse, onAuthTokenRevoked, onBiometricStaleToken = ()=>{}, onRequestOutcome = ()=>{}, getNow, readPageLoadStatus, logBIEvent, delay = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms)), random = ()=>Math.random() } = param;
    let rateLimitReportedThisTab = false;
    const reportOutcome = (param, outcome)=>{
        let { disableConnectivityTracking } = param;
        if (!disableConnectivityTracking) onRequestOutcome(outcome);
    };
    const acceptResponse = (response, param)=>{
        let { prevCookie, options } = param;
        if (!response.status) {
            throw new AxiosError('Network Error', AxiosError.ERR_NETWORK, response.config, response.request);
        }
        reportOutcome(options, 'success');
        if (prevCookie !== getCookie()) {
            onCookieChange();
        }
        if (getState().session.useAuthorizationHeader) {
            const authToken = authTokenInBody(response.data);
            if (authToken != null) onAuthTokenInResponse(authToken);
        }
        return response;
    };
    const handleAccessFailure = (e, request, state)=>{
        var _state_claims;
        const { fullUrl, options } = request;
        if (e.status === 401) {
            if (!wasSentWithCurrentToken(request, state)) return true;
            if (state.session.useAuthorizationHeader) {
                // WHY: the bearer token is sent to every host, so a stale one 401s on any of them — purge it
                // everywhere or the next boot re-hydrates the dead token and loops (ANCR-3674). The biometric
                // disable must fire before onAuthTokenRevoked, which clears the restored marker it reads.
                onBiometricStaleToken();
                onAuthTokenRevoked();
            }
            onLogout();
            return true;
        }
        if (e.status !== 403) return false;
        const isDuringBusinessSwitch = !!state.isSwitchingBusiness || request.identityAtSend.isSwitchingBusiness;
        const isFromStaleBusinessUrl = isUrlBusinessIdStale(fullUrl, (_state_claims = state.claims) === null || _state_claims === void 0 ? void 0 : _state_claims.businessId);
        const { redirectOn403 } = options;
        const isRedirectRequested = typeof redirectOn403 === 'function' ? redirectOn403(e) : !!redirectOn403;
        const navigatesToNoAccess = isRedirectRequested && !isDuringBusinessSwitch && !isFromStaleBusinessUrl;
        if (navigatesToNoAccess) onNoAccess();
        return navigatesToNoAccess || isDuringBusinessSwitch || isFromStaleBusinessUrl || selectors.user.isReviewer(state);
    };
    const isRateLimitReportAllowed = ()=>!rateLimitReportedThisTab && readPageLoadStatus().visibilityState === 'visible';
    const reportToSentry = (e, param)=>{
        let { options, fullUrl, requestId, identityAtSend } = param;
        const { method, urlForSentry, apiHost } = options;
        const serverErrorCode = e.serverErrorCode();
        const fingerprint = "Request (".concat(method.toUpperCase(), " ").concat(urlForSentry || fullUrl, ") failed with status code ").concat(e.status);
        onReportToSentry(_object_spread({
            message: "".concat(fingerprint).concat(serverErrorCode ? " (error code: ".concat(serverErrorCode, ")") : ''),
            fingerprint,
            requestId,
            method,
            url: fullUrl,
            responseBody: e.body ? JSON.stringify(e.body) : '',
            apiHost
        }, identityAtSend));
    };
    const handleFailure = (err, request)=>{
        var _request_options_disableSentry, _request_options;
        const e = new ErrorResponse(err);
        reportOutcome(request.options, e.classifyRequestFailure());
        const state = getState();
        const isRateLimited = e.isTooManyAttemptsError();
        const isExpectedAccessFailure = handleAccessFailure(e, request, state);
        const isReportSuppressed = isExpectedAccessFailure || !!selectors.impersonatingAs(state) || !!((_request_options_disableSentry = (_request_options = request.options).disableSentry) === null || _request_options_disableSentry === void 0 ? void 0 : _request_options_disableSentry.call(_request_options, e)) || isRateLimited && !isRateLimitReportAllowed();
        if (isReportSuppressed || !e.status) return;
        if (isRateLimited) rateLimitReportedThisTab = true;
        reportToSentry(e, request);
    };
    const sendRequest = function sendRequest(options) {
        const stateAtSend = getState();
        const request = prepareRequest(options, stateAtSend, getCookie);
        const isReplayableRead = READ_METHODS.includes(options.method) && !options.disableRetry;
        const isSameSession = ()=>wasSentWithCurrentToken(request, getState());
        return sendWithReplays(()=>axios.request(request.axiosConfig), {
            canReplay: isSameSession,
            replaysGatewayFailures: isReplayableRead,
            replayRefusal: options.replayRefusal,
            signal: request.axiosConfig.signal,
            onRateLimitRecovered: (recovery)=>logBIEvent(rateLimitRecoveredEvent(options, recovery))
        }, {
            delay,
            random,
            getNow
        }).then((response)=>acceptResponse(response, request)).catch((err)=>{
            var _options_config;
            if (((_options_config = options.config) === null || _options_config === void 0 ? void 0 : _options_config.responseType) !== 'blob') {
                handleFailure(err, request);
                throw err;
            }
            return ErrorResponse.withReadableBody(err).then((readableError)=>{
                handleFailure(readableError, request);
                throw readableError;
            });
        });
    };
    const sendStream = async (options)=>{
        const state = getState();
        const { fullUrl, headers, requestId, prevCookie, sendsCookies } = buildRequestInputs({
            apiHost: options.apiHost,
            url: options.url,
            disableAuth: options.disableAuth,
            authToken: undefined,
            forceAuthorizationHeader: false
        }, state, getCookie);
        await fetchEventSource(fullUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(options.data),
            credentials: sendsCookies ? 'include' : 'omit',
            signal: options.signal,
            openWhenHidden: true,
            onopen: async (res)=>{
                var _res_headers_get;
                if (prevCookie !== getCookie()) onCookieChange();
                if (res.status === 401) {
                    onBiometricStaleToken();
                    onLogout();
                    throw new FatalStreamError('unauthorized', 401);
                }
                if (res.status === 403) {
                    onNoAccess();
                    throw new FatalStreamError('forbidden', 403);
                }
                if (!res.ok) {
                    // WHY the body is read before throwing: the server answers a refused tap with a JSON error
                    // code and no stream, so discarding it here is what leaves the pane with only a bare status.
                    const refusal = await res.json().catch(()=>null);
                    const errorCode = typeof refusal === 'object' && refusal !== null && 'error' in refusal ? String(refusal.error) : undefined;
                    throw new FatalStreamError("stream open failed: ".concat(res.status), res.status, errorCode);
                }
                const contentType = (_res_headers_get = res.headers.get('content-type')) !== null && _res_headers_get !== void 0 ? _res_headers_get : '';
                if (!contentType.includes('text/event-stream')) {
                    throw new FatalStreamError('expected text/event-stream');
                }
            },
            onmessage: (evt)=>{
                if (evt.event === 'done') return;
                const parsed = options.parseEvent(evt);
                if (parsed == null) return;
                options.onEvent(parsed);
            },
            onerror: (err)=>{
                const isAuthErr = err instanceof FatalStreamError && (err.status === 401 || err.status === 403);
                const isImpersonating = !!selectors.impersonatingAs(state);
                const appErr = err instanceof ErrorResponse ? err : new ErrorResponse({
                    response: {
                        status: err instanceof FatalStreamError ? err.status : undefined,
                        // WHY: the refusal code is what tells an expected decline from a real failure.
                        data: err instanceof FatalStreamError ? {
                            error: err.errorCode
                        } : undefined
                    }
                });
                const disableByPredicate = typeof options.disableSentry === 'function' && options.disableSentry(appErr);
                const shouldReport = !isAuthErr && !isImpersonating && !disableByPredicate;
                if (shouldReport) {
                    var _state_claims, _state_userInfo;
                    const fingerprint = "Stream (POST ".concat(options.urlForSentry || fullUrl, ") failed");
                    onReportToSentry({
                        message: "".concat(fingerprint, ": ").concat(err),
                        fingerprint,
                        requestId,
                        method: 'POST',
                        url: fullUrl,
                        responseBody: '',
                        apiHost: options.apiHost,
                        claimsBusinessId: ((_state_claims = state.claims) === null || _state_claims === void 0 ? void 0 : _state_claims.businessId) || null,
                        userInfoBusinessId: ((_state_userInfo = state.userInfo) === null || _state_userInfo === void 0 ? void 0 : _state_userInfo.businessId()) || null,
                        isSwitchingBusiness: !!state.isSwitchingBusiness
                    });
                }
                throw err;
            }
        });
    };
    return {
        sendRequest,
        sendStream
    };
}
